import {
  DECAY_BASE_THRESHOLD,
  DECAY_MIN_THRESHOLD,
  DECAY_SCALE_SCORE,
  Family,
} from '../config';
import { ElementInstance, getElementDef } from '../data/elements';
import { Grid } from './Grid';

/**
 * DecaySystem manages element aging and corruption.
 * After each merge, all non-corrupted elements on the board age by 1.
 * When an element's age reaches the decay threshold, it becomes corrupted
 * (unmergeable blocker). The threshold decreases as the player's score rises,
 * creating escalating pressure.
 */
export class DecaySystem {
  private grid: Grid;
  /** Per-run modifier adjustment (e.g. Volatile → -3). Negative = corrupt sooner. */
  private thresholdOffset: number = 0;

  constructor(grid: Grid) {
    this.grid = grid;
  }

  /** Apply a per-run adjustment to the computed decay threshold. */
  setThresholdOffset(offset: number): void {
    this.thresholdOffset = offset;
  }

  /**
   * Calculate the current decay threshold based on score.
   * Starts at DECAY_BASE_THRESHOLD, linearly decreases to DECAY_MIN_THRESHOLD
   * as score approaches DECAY_SCALE_SCORE. The result is clamped to at least
   * 1 merge so a large negative offset can't make elements corrupt the instant
   * they spawn.
   */
  getThreshold(score: number): number {
    const t = Math.min(score / DECAY_SCALE_SCORE, 1);
    const base = Math.round(DECAY_BASE_THRESHOLD - t * (DECAY_BASE_THRESHOLD - DECAY_MIN_THRESHOLD));
    return Math.max(1, base + this.thresholdOffset);
  }

  /**
   * Age all non-corrupted elements on the board by 1.
   * Returns a list of elements that just became corrupted.
   */
  ageAll(score: number): ElementInstance[] {
    const threshold = this.getThreshold(score);
    const newlyCorrupted: ElementInstance[] = [];
    const elements = this.grid.getAll();

    for (const el of elements) {
      if (el.corrupted) continue;
      el.age++;
      if (el.age >= threshold) {
        el.corrupted = true;
        newlyCorrupted.push(el);
      }
    }

    return newlyCorrupted;
  }

  /**
   * Remove a corrupted element from the grid (e.g., via splinter splash).
   * Returns true if the element was corrupted and removed.
   */
  removeCorruted(col: number, row: number): boolean {
    const el = this.grid.get(col, row);
    if (!el || !el.corrupted) return false;
    this.grid.remove(col, row);
    return true;
  }

  /**
   * Positional healing (review fix #4): when a merge of `family` lands at
   * (col, row), every 8-direction neighbor of the same family has its age
   * reduced by 1 (clamped at 0). Corrupted neighbors are NOT healed — corruption
   * is permanent and must be cleared by splintering.
   *
   * Returns the list of elements that were actually healed (age decreased).
   * The matching `Family.Wild` is treated specially: a wild result heals any
   * adjacent non-corrupted neighbor regardless of family, mirroring how wilds
   * already merge across families.
   */
  healNeighbors(col: number, row: number, family: Family): ElementInstance[] {
    const healed: ElementInstance[] = [];
    const neighbors = this.grid.getNeighbors8(col, row);

    for (const { element } of neighbors) {
      if (element.corrupted) continue;
      if (element.age <= 0) continue;

      const def = getElementDef(element.defId);
      if (!def) continue;

      const sameFamily = def.family === family;
      const wildBridge = family === Family.Wild || def.family === Family.Wild;
      if (!sameFamily && !wildBridge) continue;

      element.age -= 1;
      healed.push(element);
    }

    return healed;
  }
}
